import { useCallback, useEffect, useState } from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STORAGE_KEY = "openclaw_control_theme";

type ThemeFamily = "claw" | "knot" | "dash";
type ThemeMode = "light" | "dark" | "system";

interface ThemeSelection {
  theme: ThemeFamily;
  mode: ThemeMode;
}

const THEME_LABELS: Record<ThemeFamily, string> = { claw: "Claw", knot: "Knot", dash: "Dash" };
const THEME_COLORS: Record<ThemeFamily, string> = { claw: "#ff5c5c", knot: "#4f8ff7", dash: "#d4915c" };
const MODE_ICONS: Record<ThemeMode, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };
const MODE_LABELS: Record<ThemeMode, string> = { light: "Light", dark: "Dark", system: "System" };

function resolveSystemMode(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function applyTheme(family: ThemeFamily, mode: ThemeMode) {
  const el = document.documentElement;
  const resolved = mode === "system" ? resolveSystemMode() : mode;

  let themeAttr: string | null = null;
  if (family === "knot") {themeAttr = resolved === "dark" ? "openknot" : "openknot-light";}
  else if (family === "dash") {themeAttr = resolved === "dark" ? "dash" : "dash-light";}

  if (themeAttr) {el.setAttribute("data-theme", themeAttr);}
  else {el.removeAttribute("data-theme");}

  el.setAttribute("data-theme-mode", resolved);
}

function loadSelection(): ThemeSelection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ThemeSelection;
      if (parsed.theme && parsed.mode) {return parsed;}
    }
  } catch { /* ignore */ }
  return { theme: "claw", mode: "dark" };
}

function saveSelection(sel: ThemeSelection) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sel));
}

/** Apply stored theme and listen for system-preference changes. Call once at app root. */
export function initThemeOnMount(): (() => void) | undefined {
  const sel = loadSelection();
  applyTheme(sel.theme, sel.mode);
  if (sel.mode !== "system") return;
  const mq = window.matchMedia("(prefers-color-scheme: light)");
  const handler = () => applyTheme(sel.theme, "system");
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}

function useThemeSelection() {
  const [selection, setSelection] = useState<ThemeSelection>(loadSelection);

  useEffect(() => {
    applyTheme(selection.theme, selection.mode);
    saveSelection(selection);
  }, [selection]);

  useEffect(() => {
    if (selection.mode !== "system") {return;}
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = () => applyTheme(selection.theme, "system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [selection.theme, selection.mode]);

  const selectTheme = useCallback((theme: ThemeFamily) => {
    setSelection((prev) => ({ ...prev, theme }));
  }, []);

  const selectMode = useCallback((mode: ThemeMode) => {
    setSelection((prev) => ({ ...prev, mode }));
  }, []);

  return { selection, selectTheme, selectMode };
}

export function ThemeToggle() {
  const { selection } = useThemeSelection();
  const CurrentModeIcon = MODE_ICONS[selection.mode];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Theme settings">
          <CurrentModeIcon size={15} strokeWidth={1.8} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <ThemeMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ThemeMenuItems() {
  const { selection, selectTheme, selectMode } = useThemeSelection();

  return (
    <>
      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
        Theme
      </DropdownMenuLabel>
      <DropdownMenuGroup>
        {(["claw", "knot", "dash"] as const).map((family) => (
          <DropdownMenuItem
            key={family}
            onClick={() => selectTheme(family)}
            className={selection.theme === family ? "font-medium" : ""}
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
              style={{ background: THEME_COLORS[family] }}
            />
            {THEME_LABELS[family]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
        Mode
      </DropdownMenuLabel>
      <DropdownMenuGroup>
        {(["light", "dark", "system"] as const).map((mode) => {
          const Icon = MODE_ICONS[mode];
          return (
            <DropdownMenuItem
              key={mode}
              onClick={() => selectMode(mode)}
              className={selection.mode === mode ? "font-medium" : ""}
            >
              <Icon size={13} strokeWidth={1.8} />
              {MODE_LABELS[mode]}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuGroup>
    </>
  );
}
